import React, { useState, useEffect, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import Navbar from './components/layout/Navbar';
import SignInPage from './components/auth/SignIn';
import SignUpPage from './components/auth/Signup';
import SSOCallback from './components/auth/SSOCallback';
import ContinueSocialAuth from './components/auth/ContinueSocialAuth';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { debugEnvVars, validateClerkKey } from './utils/debug';

const Home = React.lazy(() => import('./pages/Home'));
const Categories = React.lazy(() => import('./pages/Categories'));
const ProductList = React.lazy(() => import('./pages/ProductList'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const Cart = React.lazy(() => import('./pages/Cart'));
const Wishlist = React.lazy(() => import('./pages/Wishlist'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Admin = React.lazy(() => import('./pages/Admin'));
const Payment = React.lazy(() => import('./components/payment/PaymentGateway'));

// Notification Component
const Notification = ({ message, type, isVisible, onClose }) => {
  if (!isVisible) return null;

  const borderColor = {
    success: 'border-[#e2e8f0]',
    error: 'border-red-500',
    warning: 'border-amber-400',
    info: 'border-blue-500'
  }[type] || 'border-[#334155]';

  return (
    <div className="fixed top-24 right-4 z-[200] animate-slide-down">
      <div className={`glass border ${borderColor} text-[#ffffff] px-6 py-4 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.8)] max-w-sm min-w-[260px]`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              type === 'success' ? 'bg-[#e2e8f0]' :
              type === 'error' ? 'bg-red-400' :
              type === 'warning' ? 'bg-amber-400' : 'bg-blue-400'
            }`} />
            <span className="text-sm font-medium">{message}</span>
          </div>
          <button
            onClick={onClose}
            className="text-[#64748b] hover:text-[#e2e8f0] transition-colors text-lg leading-none flex-shrink-0"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

if (process.env.NODE_ENV === 'development') {
  debugEnvVars();
}

function App() {
  const [notification, setNotification] = useState({
    message: '',
    type: 'success',
    isVisible: false
  });
  // Ref to track the active dismiss timer — prevents multiple concurrent timers
  // from closing newer notifications prematurely (memory leak fix)
  const notifTimerRef = useRef(null);

  useEffect(() => {
    const handleShowNotification = (event) => {
      const { message, type = 'success' } = event.detail;
      setNotification({
        message,
        type,
        isVisible: true
      });

      // Clear any in-flight dismiss timer before starting a fresh one
      if (notifTimerRef.current) {
        clearTimeout(notifTimerRef.current);
      }
      notifTimerRef.current = setTimeout(() => {
        setNotification(prev => ({ ...prev, isVisible: false }));
        notifTimerRef.current = null;
      }, 3000);
    };

    window.addEventListener('showNotification', handleShowNotification);
    return () => {
      window.removeEventListener('showNotification', handleShowNotification);
      // Clean up any pending timer when the component unmounts
      if (notifTimerRef.current) {
        clearTimeout(notifTimerRef.current);
      }
    };
  }, []);

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  const clerkPublishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
  const isClerkKeyValid = validateClerkKey(clerkPublishableKey);
  
  if (!isClerkKeyValid) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-red-400 mb-4">Configuration Error</h1>
          <p className="text-[#94a3b8] mb-4">Clerk publishable key is missing.</p>
        </div>
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ClerkProviderWithRoutes clerkPublishableKey={clerkPublishableKey} notification={notification} closeNotification={closeNotification} />
    </Router>
  );
}

// Lightweight Page Skeleton fallback for smooth route transitions (prevents full-screen layout thrashing)
const PageSkeleton = () => (
  <div className="min-h-[70vh] max-w-7xl mx-auto px-6 pt-12 animate-pulse space-y-8">
    <div className="h-12 w-64 bg-white/5 rounded-2xl" />
    <div className="h-4 w-96 bg-white/5 rounded-lg" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 pt-8">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="aspect-square bg-white/5 rounded-[2.5rem] border border-white/5" />
      ))}
    </div>
  </div>
);

function ClerkProviderWithRoutes({ clerkPublishableKey, notification, closeNotification }) {
  const location = useLocation();
  const isAuthPath = location.pathname.startsWith('/sign-in') || 
                    location.pathname.startsWith('/sign-up') ||
                    location.pathname.startsWith('/sso-callback') ||
                    location.pathname.startsWith('/continue-social-auth');

  return (
    <ClerkProvider 
      publishableKey={clerkPublishableKey}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignInUrl="/"
      afterSignUpUrl="/"
    >
      <AuthProvider>
        <CartProvider>
          <SiteSettingsProvider>
            <div className="min-h-screen flex flex-col bg-[#020617] text-[#f8fafc] spatial-mask color-soften">
              {!isAuthPath && <Navbar />}
              <div className={`flex-grow ${!isAuthPath ? 'pt-16' : ''}`}>
                <Routes>
                  <Route path="/sign-in/sso-callback" element={<SSOCallback />} />
                  <Route path="/sign-up/sso-callback" element={<SSOCallback />} />
                  <Route path="/sso-callback" element={<SSOCallback />} />
                  <Route path="/continue-social-auth" element={<ContinueSocialAuth />} />
                  <Route path="/sign-in/*" element={<SignInPage />} />
                  <Route path="/sign-up/*" element={<SignUpPage />} />
                  
                  {/* Granular Suspense Boundaries to prevent full-screen layout thrashing */}
                  <Route path="/" element={<Suspense fallback={<PageSkeleton />}><Home /></Suspense>} />
                  <Route path="/categories" element={<Suspense fallback={<PageSkeleton />}><Categories /></Suspense>} />
                  <Route path="/products" element={<Suspense fallback={<PageSkeleton />}><ProductList /></Suspense>} />
                  <Route path="/products/:id" element={<Suspense fallback={<PageSkeleton />}><ProductDetail /></Suspense>} />
                  <Route path="/cart" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><Cart /></Suspense></ProtectedRoute>} />
                  <Route path="/wishlist" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><Wishlist /></Suspense></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><Profile /></Suspense></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><Admin /></Suspense></ProtectedRoute>} />
                  <Route path="/payment" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><Payment /></Suspense></ProtectedRoute>} />
                  <Route path="/contact" element={<Suspense fallback={<PageSkeleton />}><Contact /></Suspense>} />
                </Routes>
              </div>
              
              <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={closeNotification}
              />
            </div>
          </SiteSettingsProvider>
        </CartProvider>
      </AuthProvider>
    </ClerkProvider>
  );
}

export default App;
